// Type definitions for dashboard widgets

export interface DashboardWidget {
  id: string
  type: 'chart' | 'table' | 'metric' | 'custom'
  title: string
  description?: string
  config: WidgetConfig
  data?: any // Widget-specific data
  visible: boolean
  order: number
  createdAt: Date
  updatedAt: Date
  // Grid layout
  colSpan?: number // 1-4 columns
  rowSpan?: number // Height in grid units
}

export interface WidgetConfig {
  // Chart config
  chartType?: 'area' | 'bar' | 'line' | 'pie'
  xAxisKey?: string
  dataKeys?: string[]
  colors?: string[]
  height?: number
  showLegend?: boolean
  showGrid?: boolean
  
  // Table config
  columns?: Array<{
    key: string
    label: string
    formatter?: string
  }>
  maxRows?: number
  
  // Metric config
  metrics?: Array<{
    label: string
    value: string | number
    change?: number
    changeLabel?: string
  }>
  layout?: 'horizontal' | 'vertical' | 'grid'
  
  // Custom component config
  componentType?: string // e.g., 'traffic-chart', 'referral-pie', etc.
  componentProps?: Record<string, any>
}

export interface WidgetTemplate {
  id: string
  type: 'chart' | 'table' | 'metric' | 'custom'
  title: string
  description: string
  icon?: string
  category: 'traffic' | 'engagement' | 'conversion' | 'custom'
  defaultConfig: WidgetConfig
  preview?: React.ComponentType<any>
}

// Widget registry for built-in widgets
export const BUILT_IN_WIDGETS: WidgetTemplate[] = [
  {
    id: 'traffic-chart',
    type: 'chart',
    title: 'Traffic Chart',
    description: 'View pageviews and sessions over time',
    category: 'traffic',
    defaultConfig: {
      chartType: 'area',
      xAxisKey: 'date',
      dataKeys: ['pageviews', 'sessions'],
      height: 300,
      showLegend: true,
      showGrid: true,
      componentType: 'traffic-chart',
    },
  },
  {
    id: 'top-pages',
    type: 'table',
    title: 'Top Pages',
    description: 'Most visited pages',
    category: 'traffic',
    defaultConfig: {
      columns: [
        { key: 'page', label: 'Page' },
        { key: 'views', label: 'Views', formatter: 'number' },
        { key: 'sessions', label: 'Sessions', formatter: 'number' },
      ],
      maxRows: 10,
      componentType: 'top-pages',
    },
  },
  {
    id: 'key-metrics',
    type: 'metric',
    title: 'Key Metrics',
    description: 'Important performance metrics',
    category: 'traffic',
    defaultConfig: {
      metrics: [
        { label: 'Pageviews', value: 0 },
        { label: 'Sessions', value: 0 },
        { label: 'Bounce Rate', value: '0%' },
      ],
      layout: 'grid',
      componentType: 'key-metrics',
    },
  },
  {
    id: 'referral-sources',
    type: 'chart',
    title: 'Referral Sources',
    description: 'Traffic sources breakdown',
    category: 'traffic',
    defaultConfig: {
      chartType: 'pie',
      dataKeys: ['count'],
      height: 300,
      showLegend: true,
      componentType: 'referral-sources',
    },
  },
  {
    id: 'forms-summary',
    type: 'table',
    title: 'Forms Summary',
    description: 'Form submission analytics',
    category: 'conversion',
    defaultConfig: {
      columns: [
        { key: 'form', label: 'Form' },
        { key: 'submissions', label: 'Submissions', formatter: 'number' },
        { key: 'conversion_rate', label: 'Conversion Rate', formatter: 'percentage' },
      ],
      maxRows: 10,
      componentType: 'forms-summary',
    },
  },
  {
    id: 'click-events',
    type: 'table',
    title: 'Click Events',
    description: 'Most clicked elements',
    category: 'engagement',
    defaultConfig: {
      columns: [
        { key: 'element', label: 'Element' },
        { key: 'clicks', label: 'Clicks', formatter: 'number' },
        { key: 'page', label: 'Page' },
      ],
      maxRows: 10,
      componentType: 'click-events',
    },
  },
  {
    id: 'conversion-funnel',
    type: 'chart',
    title: 'Conversion Funnel',
    description: 'Visualize conversion funnel',
    category: 'conversion',
    defaultConfig: {
      chartType: 'bar',
      dataKeys: ['conversions'],
      height: 300,
      showLegend: false,
      componentType: 'conversion-funnel',
    },
  },
  {
    id: 'device-breakdown',
    type: 'chart',
    title: 'Device Breakdown',
    description: 'Traffic by device type',
    category: 'traffic',
    defaultConfig: {
      chartType: 'pie',
      dataKeys: ['count'],
      height: 300,
      showLegend: true,
      componentType: 'device-breakdown',
    },
  },
]






